/**
 * Public portfolio page renderer (Solution B). Mirror of the advisor's PortfolioPageRenderer
 * but uses the public flow's own conditional evaluation system (formValues-based, reactive).
 *
 * Each component is rendered with the same shared widgets (surface from the public session context) that the advisor
 * renderer uses. The difference is the conditional display wrapper — this renderer uses the
 * public evaluateCondition from public-flow-utils.ts instead of the advisor's WizardPageRenderer.
 */

import type { ConditionalDisplayConfig } from "@repo/shared";
import {
  DEFAULT_CURRENCY,
  followupComparisonVariant,
  hiddenTabIds,
  isFollowupPortfolio,
} from "@repo/shared";
import type { GoalPortfolioResponse } from "@repo/shared/types";
import { useCallback } from "react";
import { isComponentAllowedOnPageType } from "@/components/flow/flow-component-types";
import { Label } from "@/components/ui/label";
import type { CompletionMarkers } from "@/lib/hooks/use-flow-instances/use-flow-completion";
import {
  renderEmbed,
  renderFile,
  renderIframe,
  renderModal,
  renderSpacer,
  renderTextBlock,
  renderYoutubeVideo,
} from "@/pages/clients/detail/flows/instance/flow-component-renderers";
import type { PortfolioCost } from "@/pages/clients/detail/flows/instance/flow-wizard/flow-wizard-types";
import { MisplacedComponentNotice } from "@/pages/clients/detail/flows/instance/misplaced-component-notice";
import { AssetAllocation } from "@/pages/clients/detail/flows/instance/self-contained/asset-allocation";
import { CostBenefit } from "@/pages/clients/detail/flows/instance/self-contained/cost-benefit";
import { CostChart } from "@/pages/clients/detail/flows/instance/self-contained/cost-chart";
import { CostTable } from "@/pages/clients/detail/flows/instance/self-contained/cost-table";
import { ExpectedValue } from "@/pages/clients/detail/flows/instance/self-contained/expected-value";
import { HistoricalReturn } from "@/pages/clients/detail/flows/instance/self-contained/historical-return";
import { PortfolioCosts } from "@/pages/clients/detail/flows/instance/self-contained/portfolio-costs";
import { PortfolioDisplay } from "@/pages/clients/detail/flows/instance/self-contained/portfolio-display";
import { PortfolioForecast } from "@/pages/clients/detail/flows/instance/self-contained/portfolio-forecast";
import { PortfolioProductCosts } from "@/pages/clients/detail/flows/instance/self-contained/portfolio-product-costs";
import { PortfolioXray } from "@/pages/clients/detail/flows/instance/self-contained/portfolio-xray";
import type { PortfolioInfo } from "@/pages/clients/detail/flows/instance/self-contained/types";
import { CustomPortfolioBuilder } from "@/pages/flow-instances/goals/custom-portfolio-builder";
import { FollowupSourceNotice } from "@/pages/flow-instances/goals/followup-source-notice";
import { PortfolioInputWidget } from "@/pages/flow-instances/goals/portfolio-input-widget";
import { usePublicFlowActions } from "../public-flow-context";
import { evaluateCondition, type PublicConditionEvaluationData } from "../public-flow-utils";
import { FieldRenderer } from "./field-renderer";
import { PublicImageRenderer } from "./public-image-renderer";

type PublicPortfolioPageRendererProps = {
  page: any;
  portfolio: GoalPortfolioResponse;
  goalId: string;
  goals: any[];
  isCompleted: boolean;
  flowLanguage: string;
  formValues: Record<string, string>;
  /** QPBI-395 — flow field id → field type, for type-aware conditional rules. */
  fieldTypes?: Record<string, string>;
  chartColors?: string[] | null;
  /** QL-403 — the collection's portfolio cost brackets, shown by `portfolio_costs`. */
  portfolioCosts?: PortfolioCost[];
  /** Flow currency the portfolio cost brackets are shown in. */
  currency?: string;
  showValidationErrors?: boolean;
  markers: CompletionMarkers;
  /**
   * QP-185 — the collection's opt-in dynamic setting; gates the "dynamic"
   * option in the portfolio type selector (disabled + annotation when off).
   */
  dynamicPortfoliosEnabled: boolean;
  /** QL-72 — shows the model-portfolio picker on model portfolios. */
  modelDeviationEnabled?: boolean;
};

export function PublicPortfolioPageRenderer({
  page,
  portfolio,
  goalId,
  goals,
  isCompleted,
  flowLanguage,
  formValues,
  fieldTypes,
  chartColors,
  portfolioCosts = [],
  currency = DEFAULT_CURRENCY,
  showValidationErrors,
  markers,
  dynamicPortfoliosEnabled,
  modelDeviationEnabled = false,
}: PublicPortfolioPageRendererProps) {
  const goal = goals.find((g: any) => g.id === goalId) ?? null;
  // Suitability submit-blocker feed — the public shell gates Submit on it.
  const { setSuitabilityBlocker } = usePublicFlowActions();
  const portfolioId: string | undefined = portfolio?.id;

  // Stable identity — the builder's effects depend on this callback; a fresh
  // inline closure per render would ping-pong its mount/cleanup effects into
  // an infinite loop. Re-keys by portfolioId (several portfolios per goal).
  const handleSuitabilityBlockerChange = useCallback(
    (_goalId: string, hasBlocker: boolean) => {
      if (portfolioId) setSuitabilityBlocker(portfolioId, hasBlocker);
    },
    [setSuitabilityBlocker, portfolioId],
  );

  if (!portfolio || !portfolioId) return null;

  // Both follow-up lineages light up the existing-vs-modified comparison
  // views; the shared predicates are the single definition of the rule.
  const isFollowup = isFollowupPortfolio(portfolio as any);
  const comparisonVariant = followupComparisonVariant(portfolio as any);

  const conditionData: PublicConditionEvaluationData = {
    formValues,
    goals,
    fieldTypes,
    goalId,
    portfolioId,
  };

  const portfolioInfo: PortfolioInfo = {
    isCustomPortfolio: portfolio.portfolioType === "custom",
    hasRiskAssessment: !!goal?.hasRiskAssessment,
    hasInvestmentAmount:
      Number(portfolio.oneTimeAmount ?? 0) > 0 || Number(portfolio.recurringAmount ?? 0) > 0,
    riskScore: goal?.riskScore ?? null,
  };

  const renderComponent = (component: any) => {
    const componentHasError = !!(
      showValidationErrors &&
      markers.missingPortfolioComponentKeys?.has(`${portfolioId}:${component.id}`)
    );

    if (!isComponentAllowedOnPageType(component.componentType, "portfolio")) {
      return (
        <MisplacedComponentNotice
          componentType={component.componentType}
          pageType="portfolio"
          language={flowLanguage}
        />
      );
    }

    const cfg = component.config ? JSON.parse(component.config) : {};

    switch (component.componentType) {
      case "portfolio_input": {
        const allowedPortfolioTypes = cfg.allowedPortfolioTypes || ["model", "custom", "dynamic"];
        return (
          <PortfolioInputWidget
            goalId={goalId}
            portfolioId={portfolioId}
            initialData={{
              name: portfolio.name ?? "",
              scheduleType: portfolio.scheduleType,
              oneTimeAmount: portfolio.oneTimeAmount ?? undefined,
              oneTimeDate: portfolio.oneTimeDate,
              recurringAmount: portfolio.recurringAmount ?? undefined,
              recurringFrequency: portfolio.recurringFrequency,
              recurringStartDate: portfolio.recurringStartDate,
              recurringEndDate: portfolio.recurringEndDate,
              portfolioType: (portfolio.portfolioType ?? "model") as "model" | "custom" | "dynamic",
            }}
            disabled={isCompleted}
            followupSource={portfolio.followupSource ?? null}
            showPortfolioName={cfg.showPortfolioName !== false}
            showPortfolioType={cfg.showPortfolioType !== false}
            allowedPortfolioTypes={allowedPortfolioTypes}
            dynamicPortfoliosEnabled={dynamicPortfoliosEnabled}
            modelDeviationEnabled={modelDeviationEnabled}
            pickedPortfolioId={portfolio.pickedPortfolioId ?? null}
            // QL-664 — the same custom-only gate the advisor sees; the server
            // refuses model / dynamic either way.
            templateRestricted={goal?.templateRestricted ?? false}
            showDateInputs={cfg.showDateInputs}
            style={cfg.style}
            title={cfg.title}
            description={cfg.description}
            hasValidationError={componentHasError}
            missingReason={markers.missingComponentReasons.get(`${portfolioId}:${component.id}`)}
            goalRiskScore={goal?.riskScore ?? null}
            minBuyViolations={portfolio.minBuyViolations ?? []}
            accountMinimumViolations={portfolio.accountMinimumViolations ?? []}
            generatedPositions={portfolio.generatedPositions ?? []}
            dynamicGeneration={portfolio.dynamicGeneration ?? null}
          />
        );
      }
      case "custom_portfolio":
        return (
          <CustomPortfolioBuilder
            portfolioId={portfolioId}
            goalId={goalId}
            style={cfg.style}
            title={cfg.title}
            description={cfg.description}
            showIsin={cfg.showIsin}
            showProductType={cfg.showProductType}
            onlyAllowSuitableProducts={cfg.onlyAllowSuitableProducts}
            goals={goals}
            isCompleted={isCompleted}
            hasValidationError={componentHasError}
            missingReason={markers.missingComponentReasons.get(`${portfolioId}:${component.id}`)}
            onSuitabilityBlockerChange={handleSuitabilityBlockerChange}
          />
        );
      case "portfolio_display":
        return (
          <PortfolioDisplay
            portfolioId={portfolioId}
            config={cfg}
            language={flowLanguage}
            chartColors={chartColors}
            portfolioInfo={portfolioInfo}
            hasValidationError={componentHasError}
          />
        );
      case "portfolio_xray":
        return (
          <PortfolioXray
            portfolioId={portfolioId}
            config={cfg}
            language={flowLanguage}
            chartColors={chartColors}
            portfolioInfo={portfolioInfo}
            hasValidationError={componentHasError}
            showFollowupComparison={isFollowup && cfg.enableFollowupComparison === true}
            comparisonVariant={comparisonVariant}
          />
        );
      case "asset_allocation":
        return (
          <AssetAllocation
            portfolioId={portfolioId}
            config={cfg}
            language={flowLanguage}
            chartColors={chartColors}
            portfolioInfo={portfolioInfo}
            hasValidationError={componentHasError}
            showFollowupComparison={isFollowup && cfg.enableFollowupComparison === true}
            comparisonVariant={comparisonVariant}
          />
        );
      case "historical_return":
        return (
          <HistoricalReturn
            portfolioId={portfolioId}
            config={cfg}
            language={flowLanguage}
            portfolioInfo={portfolioInfo}
            hasValidationError={componentHasError}
          />
        );
      case "cost_table":
        return (
          <CostTable
            portfolioId={portfolioId}
            config={cfg}
            language={flowLanguage}
            portfolioInfo={portfolioInfo}
            hasValidationError={componentHasError}
          />
        );
      case "cost_chart":
        return (
          <CostChart
            portfolioId={portfolioId}
            config={cfg}
            language={flowLanguage}
            portfolioInfo={portfolioInfo}
            chartColors={chartColors}
            hasValidationError={componentHasError}
          />
        );
      case "cost_benefit":
        return (
          <CostBenefit
            portfolioId={portfolioId}
            config={cfg}
            portfolioInfo={portfolioInfo}
            isOneTimePortfolio={portfolio.scheduleType === "one_time"}
            comparisonVariant={comparisonVariant}
            hasValidationError={componentHasError}
          />
        );
      case "portfolio_forecast":
        return (
          <PortfolioForecast
            portfolioId={portfolioId}
            config={cfg}
            language={flowLanguage}
            hasValidationError={componentHasError}
          />
        );
      case "expected_value":
        return (
          <ExpectedValue
            portfolioId={portfolioId}
            config={cfg}
            language={flowLanguage}
            portfolioInfo={portfolioInfo}
            hasValidationError={componentHasError}
          />
        );
      case "portfolio_costs":
        return <PortfolioCosts portfolioCosts={portfolioCosts} config={cfg} currency={currency} />;
      case "product_costs":
        return (
          <PortfolioProductCosts portfolioId={portfolioId} config={cfg} language={flowLanguage} />
        );
      case "field": {
        if (!component.flowField) return null;
        const fieldKey =
          component.flowField.scope === "portfolio"
            ? `${portfolioId}:${component.flowField.id}`
            : component.flowField.scope === "goal"
              ? `${goalId}:${component.flowField.id}`
              : component.flowField.id;
        const fieldHasError = !!(showValidationErrors && markers.missingFieldKeys?.has(fieldKey));
        return (
          <FieldRenderer
            field={component.flowField}
            goalId={goalId}
            portfolioId={portfolioId}
            isCompleted={isCompleted}
            hasValidationError={fieldHasError}
            showLabel={false}
          />
        );
      }
      case "text_block":
        return renderTextBlock(component, flowLanguage);
      case "spacer":
        return renderSpacer(component);
      case "youtube_video":
        return renderYoutubeVideo(component);
      case "iframe":
        return renderIframe(component);
      case "image":
        return <PublicImageRenderer component={component} />;
      case "embed":
        return renderEmbed(component);
      case "modal":
        return renderModal(component, flowLanguage);
      case "file":
        return renderFile(component, flowLanguage);
      default:
        return null;
    }
  };

  // Render components with the PUBLIC conditional display evaluator.
  // Unlike the advisor's WizardPageRenderer, this evaluates against formValues
  // (the optimistic in-memory store), making conditional display reactive.
  const allComponents = [
    ...(page.layouts || []).flatMap((layout: any) => {
      // Parse layout-level conditional display
      let layoutConditional: ConditionalDisplayConfig | null = null;
      try {
        const config = layout.config ? JSON.parse(layout.config) : null;
        layoutConditional = config?.conditionalDisplay || null;
      } catch {
        // ignore
      }
      if (!evaluateCondition(layoutConditional, conditionData)) return [];
      // QL-217 — drop what a tab's own rule hides.
      const hiddenTabs = hiddenTabIds(layout.config, (rule) =>
        evaluateCondition(rule, conditionData),
      );
      return (layout.components || [])
        .filter((c: any) => !(c.tabId && hiddenTabs.has(c.tabId)))
        .map((c: any) => ({ ...c, _fromLayout: true }));
    }),
    ...(page.components || []).filter((c: any) => !c.flowPageLayoutId),
  ].sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <div className="space-y-6">
      {portfolio.followupSource && <FollowupSourceNotice source={portfolio.followupSource} />}
      {allComponents.map((component: any) => {
        // Per-component conditional display
        let conditionalDisplay: ConditionalDisplayConfig | null = null;
        try {
          const config = component.config ? JSON.parse(component.config) : null;
          conditionalDisplay = config?.conditionalDisplay || null;
        } catch {
          // ignore
        }

        if (!evaluateCondition(conditionalDisplay, conditionData)) return null;

        return (
          <div key={component.id} className="space-y-2">
            {component.componentType === "field" && component.flowField && (
              <Label htmlFor={component.flowField.id}>
                {component.flowField.name}
                {component.flowField.isRequired && <span className="text-destructive ml-1">*</span>}
              </Label>
            )}
            {renderComponent(component)}
          </div>
        );
      })}
    </div>
  );
}
